import { apiRequest } from "./client";
import { normalizeListPayload } from "../utils/pagination";

export const makeSlug = (value = "") =>
  value.toString().toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

const asList = (value) => (Array.isArray(value) ? value : []);
const text = (value) => (value == null ? "" : String(value));

export const normalizeSpecs = (specs = []) =>
  asList(specs).map((item) => {
    if (Array.isArray(item)) {
      return { label: text(item[0]), label_zh: text(item[3]), value: text(item[1]), value_zh: text(item[2]) };
    }
    if (item && typeof item === "object") {
      return {
        label: text(item.label),
        label_zh: text(item.label_zh ?? item.labelZh),
        value: text(item.value),
        value_zh: text(item.value_zh ?? item.valueZh),
      };
    }
    return { label: text(item), label_zh: "", value: "", value_zh: "" };
  });

export const normalizeStandards = (standards = []) =>
  asList(standards).map((item) => {
    if (Array.isArray(item)) {
      return { name: text(item[0]), name_zh: text(item[3]), value: text(item[1]), value_zh: text(item[2]) };
    }
    if (item && typeof item === "object") {
      return {
        name: text(item.name ?? item.label),
        name_zh: text(item.name_zh ?? item.nameZh ?? item.label_zh ?? item.labelZh),
        value: text(item.value),
        value_zh: text(item.value_zh ?? item.valueZh),
      };
    }
    return { name: text(item), name_zh: "", value: "", value_zh: "" };
  });

export const normalizeApplications = (applications = []) =>
  asList(applications).map((item) => {
    if (typeof item === "string") return { name: item, name_zh: "" };
    if (Array.isArray(item)) return { name: text(item[0]), name_zh: text(item[1]) };
    if (item && typeof item === "object") {
      return {
        name: text(item.name ?? item.label ?? item.value),
        name_zh: text(item.name_zh ?? item.nameZh ?? item.label_zh ?? item.labelZh),
      };
    }
    return { name: "", name_zh: "" };
  });

const serializeSpecs = (specs = []) =>
  normalizeSpecs(specs)
    .filter((item) => item.label.trim() || item.label_zh.trim() || item.value.trim() || item.value_zh.trim())
    .map((item) => {
      const spec = { label: item.label.trim(), value: item.value.trim() };
      if (item.label_zh.trim()) spec.label_zh = item.label_zh.trim();
      if (item.value_zh.trim()) spec.value_zh = item.value_zh.trim();
      return spec;
    });

const serializeStandards = (standards = []) =>
  normalizeStandards(standards)
    .filter((item) => item.name.trim() || item.name_zh.trim() || item.value.trim() || item.value_zh.trim())
    .map((item) => {
      const standard = { name: item.name.trim(), value: item.value.trim() };
      if (item.name_zh.trim()) standard.name_zh = item.name_zh.trim();
      if (item.value_zh.trim()) standard.value_zh = item.value_zh.trim();
      return standard;
    });

const serializeApplications = (applications = []) =>
  normalizeApplications(applications)
    .filter((item) => item.name.trim() || item.name_zh.trim())
    .map((item) => {
      const application = { name: item.name.trim() };
      if (item.name_zh.trim()) application.name_zh = item.name_zh.trim();
      return application;
    });

export const buildUpdateProductPayload = (product) => ({
  name: product.name,
  name_zh: product.name_zh ?? "",
  overview: product.overview ?? "",
  overview_zh: product.overview_zh ?? "",
  slug: product.slug,
  sku: product.sku,
  category_id: product.category_id ?? null,
  featured: !!product.featured,
  most_popular_badge: !!product.most_popular_badge,
  status: product.status !== undefined ? !!product.status : true,
  visibility: !!product.visibility,
  sort_order: Number(product.sort_order) || 0,
  whatsapp_enquiry: !!product.whatsapp_enquiry,
  enquiry_enabled: !!product.enquiry_enabled,
  meta_title: product.meta_title ?? "",
  meta_description: product.meta_description ?? "",
  meta_keyword: product.meta_keyword ?? "",
  specs: serializeSpecs(product.specs),
  standards: serializeStandards(product.standards),
  applications: serializeApplications(product.applications),
  related_product_ids: product.related_product_ids || [],
});

const buildSummary = (products, summary) => {
  if (summary) return summary;

  return {
    total_products: products.length,
    visible_products: products.filter((product) => product.visibility).length,
    featured_products: products.filter((product) => product.featured).length,
    total_categories: 0,
  };
};

export const fetchProducts = async (params = {}) => {
  const search = new URLSearchParams();
  Object.entries({
    page: 1,
    per_page: 20,
    sort_by: "sort_order",
    sort_order: "desc",
    include: "images",
    ...params,
  }).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") search.set(key, value);
  });
  const query = search.toString();
  const payload = await apiRequest(`/products${query ? `?${query}` : ""}`);
  const { items: products, meta } = normalizeListPayload(payload, {
    itemKeys: ["products"],
    perPage: params.per_page,
  });
  const body = payload?.data && !Array.isArray(payload.data) ? payload.data : payload;

  return {
    products,
    meta,
    summary: buildSummary(products, body?.summary),
  };
};

export const buildCreateProductPayload = (product) => buildUpdateProductPayload(product);

export const createProduct = async (product) => {
  const payload = await apiRequest("/products", {
    method: "POST",
    body: JSON.stringify(buildCreateProductPayload(product)),
  });
  return payload.data;
};

export const fetchProduct = async (id) => (await apiRequest(`/products/${id}`)).data;

export const deleteProduct = (id) => apiRequest(`/products/${id}`, { method: "DELETE" });

export const updateProduct = async (id, product) => {
  const payload = await apiRequest(`/products/${id}`, {
    method: "PUT",
    body: JSON.stringify(buildUpdateProductPayload(product)),
  });
  return payload.data;
};

export const updateProductDisplay = async (products) => {
  const rows = (Array.isArray(products) ? products : [products]).map((product) => ({
    id: product.id,
    visibility: !!product.visibility,
    featured: !!product.featured,
    sort_order: Number(product.sort_order) || 0,
  }));

  const payload = await apiRequest("/products/display", {
    method: "PUT",
    body: JSON.stringify({ products: rows }),
  });
  return payload.data;
};

export const uploadProductImages = async (id, files, { is_base_image = false, is_thumbnail = false } = {}) => {
  const form = new FormData();
  const list = Array.from(files || []).filter((file) => file && file.size > 0);
  if (!list.length) {
    throw new Error("Choose a JPG, PNG or WebP image to upload.");
  }

  list.forEach((file, index) => {
    const name = String(file.name || `image-${index}.jpg`).replace(/[^\w.\-]+/g, "_");
    form.append(`images[${index}]`, file, name || `image-${index}.jpg`);
  });
  form.append("is_base_image", is_base_image ? "1" : "0");
  form.append("is_thumbnail", is_thumbnail ? "1" : "0");

  const payload = await apiRequest(`/products/${id}/images`, { method: "POST", body: form });
  return payload?.data ?? payload;
};

export const updateProductImage = async (productId, imageId, payload) => {
  const response = await apiRequest(`/products/${productId}/images/${imageId}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
  return response?.data ?? response;
};

export const deleteProductImage = async (productId, imageId) => {
  const response = await apiRequest(`/products/${productId}/images/${imageId}`, { method: "DELETE" });
  return response?.data ?? response;
};
